import { useState, useRef, useCallback, useEffect } from 'react';
import moment from 'moment';
import { downloadPlaybackOnDevice } from '../../../socket/playback';
import { downloadFile } from '../../../untils/download';
import { genDownloadKeyLocal } from '../utils';
import { beeXLogger } from '../../../helpers/logger';
import Downloader, { downloaderInstance } from '../downloader';

export const useDownloader = ({
  type,
  src,
  logContent,
  logSerial,
  namespace,
  serial,
  fromTime,
  toTime,
  handleReportActionLog,
  selectedDate,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [isRequestingDownload, setIsRequestingDownload] = useState(false);
  const currentDownloadKeyRef = useRef(null);

  useEffect(() => {
    const unsubscribe = downloaderInstance.subscribe(({ key, state }) => {
      if (key !== currentDownloadKeyRef.current) return;
      if (isRequestingDownload) setIsRequestingDownload(false);

      if (state.status === 'downloading') {
        setDownloadProgress(state.progress ?? 0);
        setIsDownloading(true);
      } else if (state.status === 'completed') {
        setDownloadProgress(100);
        const blob = downloaderInstance.getBlob(key);
        if (blob) {
          const parts = key.split('_');
          Downloader.downloadBlob(blob, `playback-${parts.join('-')}.mp4`);
        }
        setTimeout(() => {
          setDownloadProgress(null);
          setIsDownloading(false);
        }, 800);
      } else if (state.status === 'error' || !state) {
        setDownloadProgress(null);
        setIsDownloading(false);
      }
    });
    return unsubscribe;
  }, [isRequestingDownload]);

  const onDownloadDirectClick = useCallback(() => {
    if (!type) {
      if (!src || !logContent || !logSerial) return;
      setIsDownloading(true);
      downloadFile({
        url: src,
        fileName: JSON.parse(logContent).cameraName,
        isSaveAs: JSON.parse(logContent).isSaveAs,
        onFetched: () => setIsDownloading(false),
      });
      handleReportActionLog(logContent, logSerial);
      return;
    }

    if (type === 'on_gateway') {
      const datePart = moment(selectedDate);
      const fromTimePart = moment(fromTime, 'HH:mm:ss');
      const toTimePart = moment(toTime, 'HH:mm:ss');

      const fromTimeFormatted = datePart
        .clone()
        .set({
          hour: fromTimePart.hour(),
          minute: fromTimePart.minute(),
          second: fromTimePart.second(),
        })
        .format('YYYY-MM-DD HH:mm:ss');

      const toTimeFormatted = datePart
        .clone()
        .set({
          hour: toTimePart.hour(),
          minute: toTimePart.minute(),
          second: toTimePart.second(),
        })
        .format('YYYY-MM-DD HH:mm:ss');

      setIsRequestingDownload(true);
      downloadPlaybackOnDevice({
        namespace,
        serial,
        fromTime: fromTimeFormatted,
        toTime: toTimeFormatted,
      })
        .then((res) => {
          if (!res?.downloadUrl) return;
          let url = res.downloadUrl.includes('/playbacks')
            ? res.downloadUrl
            : res.downloadUrl.replace(serial, `playbacks / ${serial} `);
          url = url.replace('playback01.beex.vn', 'playback01.beex.vn:5199');

          const downloadKeyLocal = genDownloadKeyLocal(
            serial,
            fromTimeFormatted,
            toTimeFormatted,
          );
          setTimeout(() => {
            beeXLogger.debug('START DOWNLOAD: ', downloadKeyLocal);
            currentDownloadKeyRef.current = downloadKeyLocal;
            setIsDownloading(true);
            setDownloadProgress(0);
            downloaderInstance.download(
              downloadKeyLocal,
              url,
              res.downloadType,
              res.fileSize || 0,
            );
          }, 1000);
        })
        .catch((err) => {
          setIsRequestingDownload(false);
          beeXLogger.error('Error downloading playback on device:', err);
        });
    }
  }, [
    type,
    src,
    logContent,
    logSerial,
    namespace,
    serial,
    fromTime,
    toTime,
    handleReportActionLog,
    selectedDate,
  ]);

  return {
    isDownloading,
    downloadProgress,
    isRequestingDownload,
    onDownloadDirectClick,
  };
};
